import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Button, Stack, Title, Text, Container, Paper } from '@mantine/core';
import { captureError } from '@/lib/monitoring/sentry';
import { trackEvent } from '@/lib/analytics/analytics-client';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureError(error, {
      componentStack: errorInfo.componentStack,
      boundary: 'ErrorBoundary',
    });

    trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      error_type: error.name,
      page: window.location.pathname,
      severity: 'critical',
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container size="sm" py="xl">
          <Paper shadow="md" p="xl" radius="md" withBorder>
            <Stack align="center" gap="lg">
              <Title order={2} c="red.6">
                Something went wrong
              </Title>
              <Text c="dimmed" ta="center">
                We encountered an unexpected error. This has been logged and we're working on a fix.
              </Text>
              {import.meta.env.DEV && this.state.error && (
                <Paper bg="gray.1" p="md" radius="sm" w="100%">
                  <Text
                    size="xs"
                    c="red"
                    style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                  >
                    {this.state.error.message}
                  </Text>
                </Paper>
              )}
              <Stack gap="sm">
                <Button onClick={this.handleRetry} variant="filled">
                  Try Again
                </Button>
                <Button onClick={this.handleGoHome} variant="subtle">
                  Go to Dashboard
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}
